import React from "react";

// Elegant custom botanical SVG illustrations — hand-tuned line art in a warm ivory/gold aesthetic.
// Each variant is a stylised interpretation, not a literal representation.
// Uses stroke-based, single-color linework so it feels like a luxury apothecary journal.

const wrap = (children, extraClass = "") => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 96 96"
    fill="none"
    className={"w-full h-full " + extraClass}
    aria-hidden="true"
  >
    {children}
  </svg>
);

// Root system with rhizome — for Ashwagandha
const rootHerb = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M48 12 C 46 24, 50 32, 48 44" />
    <path d="M48 44 C 42 50, 40 58, 44 68 C 46 76, 44 82, 40 86" />
    <path d="M48 44 C 54 50, 58 60, 56 72 C 54 80, 58 84, 62 86" />
    <path d="M48 44 C 50 52, 52 62, 52 78" />
    <path d="M42 30 c -4 -2 -8 -1 -10 2" opacity="0.7" />
    <path d="M54 30 c 4 -2 8 -1 10 2" opacity="0.7" />
    <ellipse cx="48" cy="14" rx="4" ry="6" opacity="0.9" />
  </g>
);

// Tuberous cluster — for Shatavari / Vidarikand / Safed Musli
const tuberHerb = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M48 14 v 20" />
    <path d="M40 26 c -6 -2 -10 -6 -12 -12" opacity="0.7" />
    <path d="M56 26 c 6 -2 10 -6 12 -12" opacity="0.7" />
    <path d="M48 34 C 34 38, 24 50, 30 66 C 34 78, 40 82, 44 82" />
    <path d="M48 34 C 62 38, 72 50, 66 66 C 62 78, 56 82, 52 82" />
    <ellipse cx="38" cy="60" rx="6" ry="10" />
    <ellipse cx="58" cy="60" rx="6" ry="10" />
    <ellipse cx="48" cy="72" rx="5" ry="8" />
  </g>
);

// Berry cluster on stem — for Sea Buckthorn / Gokshura
const berryHerb = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 78 C 34 68, 44 54, 52 40" />
    <path d="M52 40 C 60 30, 70 22, 82 18" opacity="0.85" />
    <path d="M40 62 l 6 -8" opacity="0.6" />
    <path d="M50 50 l 6 -6" opacity="0.6" />
    <path d="M60 40 l 6 -6" opacity="0.6" />
    <circle cx="34" cy="70" r="3.2" />
    <circle cx="42" cy="60" r="3.2" />
    <circle cx="52" cy="52" r="3.2" />
    <circle cx="62" cy="42" r="3.2" />
    <circle cx="72" cy="30" r="3.2" />
    <circle cx="28" cy="76" r="2.4" opacity="0.7" />
    <circle cx="66" cy="34" r="2.4" opacity="0.7" />
  </g>
);

// Sacred leaf pair — for Tulsi
const tulsiLeaf = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M48 84 V 46" />
    <path d="M48 46 C 30 42, 18 30, 20 14 C 36 14, 48 26, 48 46 Z" />
    <path d="M48 46 C 66 42, 78 30, 76 14 C 60 14, 48 26, 48 46 Z" />
    <path d="M28 20 C 34 26, 40 32, 46 40" opacity="0.5" />
    <path d="M68 20 C 62 26, 56 32, 50 40" opacity="0.5" />
    <path d="M40 78 c -4 -2 -6 -6 -6 -10" opacity="0.7" />
    <path d="M56 78 c 4 -2 6 -6 6 -10" opacity="0.7" />
  </g>
);

// Peppercorn spike — for Pippali
const spike = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M50 12 v 40" opacity="0.6" />
    <path d="M48 52 C 34 56, 28 70, 34 84 C 40 88, 48 88, 50 86" />
    <path d="M48 52 C 62 56, 68 70, 62 84 C 56 88, 50 88, 48 86" />
    <circle cx="42" cy="60" r="2" />
    <circle cx="54" cy="60" r="2" />
    <circle cx="38" cy="68" r="2" />
    <circle cx="58" cy="68" r="2" />
    <circle cx="42" cy="76" r="2" />
    <circle cx="54" cy="76" r="2" />
    <circle cx="48" cy="82" r="2" />
    <path d="M42 12 c 4 -6 12 -6 16 0" opacity="0.7" />
  </g>
);

// Bark strip — for Arjun Bark / Mulethi
const bark = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M28 14 c 6 4 8 12 6 22 C 32 46, 30 60, 34 76 c 2 6 6 8 10 8" />
    <path d="M68 14 c -6 4 -8 12 -6 22 C 64 46, 66 60, 62 76 c -2 6 -6 8 -10 8" />
    <path d="M40 24 h 16" opacity="0.6" />
    <path d="M38 40 h 20" opacity="0.6" />
    <path d="M40 56 h 16" opacity="0.6" />
    <path d="M42 72 h 12" opacity="0.6" />
    <path d="M46 18 v 66" opacity="0.4" />
    <path d="M52 18 v 66" opacity="0.4" />
  </g>
);

// Seed pod cluster — for Kauch Beej
const seedPod = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 80 C 26 60, 40 46, 60 38" />
    <path d="M60 38 C 68 34, 78 32, 84 32" opacity="0.85" />
    <path d="M28 68 c -4 -2 -8 0 -10 4" opacity="0.6" />
    <path d="M46 52 c 2 4 6 6 10 4" opacity="0.6" />
    <ellipse cx="32" cy="60" rx="4" ry="9" transform="rotate(-25 32 60)" />
    <ellipse cx="46" cy="46" rx="4" ry="10" transform="rotate(-30 46 46)" />
    <ellipse cx="62" cy="36" rx="4" ry="10" transform="rotate(-40 62 36)" />
    <path d="M32 54 c 1 3 1 6 0 12" opacity="0.7" />
    <path d="M46 40 c 1 3 1 6 0 12" opacity="0.7" />
    <path d="M62 30 c 1 3 1 6 0 12" opacity="0.7" />
  </g>
);

// Vine — for Guduchi (Giloy)
const vine = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 20 C 30 30, 40 30, 46 46 C 52 62, 62 66, 78 78" />
    <path d="M28 24 c -2 4 -2 8 0 12" opacity="0.6" />
    <path d="M42 40 c -4 -2 -8 -2 -12 2" opacity="0.6" />
    <path d="M58 56 c 4 -2 8 0 10 4" opacity="0.6" />
    <path d="M70 70 c -4 -2 -8 -2 -10 2" opacity="0.6" />
    <path d="M24 26 c 8 -2 12 -8 12 -14" />
    <path d="M50 50 c 8 -2 12 -8 12 -14" />
    <path d="M72 74 c 8 -2 12 -8 12 -14" />
    <path d="M24 26 c 2 8 -2 14 -8 16" opacity="0.85" />
    <path d="M50 50 c 2 8 -2 14 -8 16" opacity="0.85" />
  </g>
);

// Mineral rock — for Shilajit
const mineral = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 74 L 28 46 L 40 30 L 58 26 L 74 40 L 80 60 L 74 78 L 30 80 Z" />
    <path d="M28 46 L 44 44 L 58 26" opacity="0.55" />
    <path d="M40 30 L 44 44 L 40 60 L 58 62 L 74 40" opacity="0.55" />
    <path d="M44 44 L 58 62" opacity="0.55" />
    <path d="M58 62 L 74 78" opacity="0.55" />
    <path d="M30 80 L 40 60" opacity="0.55" />
    <circle cx="52" cy="52" r="1.5" opacity="0.6" />
    <circle cx="66" cy="50" r="1.5" opacity="0.6" />
  </g>
);

// Root & flower — for Punarnava
const rootFlower = wrap(
  <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M48 14 v 34" />
    <circle cx="48" cy="18" r="3.5" />
    <circle cx="38" cy="24" r="2.5" opacity="0.8" />
    <circle cx="58" cy="24" r="2.5" opacity="0.8" />
    <circle cx="48" cy="30" r="2.5" opacity="0.6" />
    <path d="M42 32 l -8 8" opacity="0.6" />
    <path d="M54 32 l 8 8" opacity="0.6" />
    <path d="M48 48 C 34 54, 26 66, 32 80 c 4 6 10 6 12 4" />
    <path d="M48 48 C 62 54, 70 66, 64 80 c -4 6 -10 6 -12 4" />
    <path d="M40 62 c 2 4 4 8 4 14" opacity="0.55" />
    <path d="M56 62 c -2 4 -4 8 -4 14" opacity="0.55" />
  </g>
);

const REGISTRY = {
  root: rootHerb,
  tuber: tuberHerb,
  berry: berryHerb,
  tulsi: tulsiLeaf,
  spike: spike,
  bark: bark,
  seedpod: seedPod,
  vine: vine,
  mineral: mineral,
  rootflower: rootFlower,
};

export const BotanicalIcon = ({ variant = "root", className = "" }) => {
  const el = REGISTRY[variant] || REGISTRY.root;
  return <div className={"text-[var(--hk-orange-deep)] " + className}>{el}</div>;
};

export default BotanicalIcon;
